import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Radio, Trophy, MapPin, CalendarDays, ExternalLink, ChevronLeft, ChevronRight, Home as HomeIcon, Users, Play } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Seo, SITE_URL } from "@/components/Seo";
import { RichText } from "@/components/RichText";
import { TournamentSignup } from "@/components/TournamentSignup";
import { TournamentPlayerList } from "@/components/TournamentPlayerList";
import { useSpotsLabel } from "@/lib/useSpotsLabel";
import { track } from "@/lib/analytics";

const clip = (html, n = 155) => {
  const txt = (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return txt.length > n ? txt.slice(0, n - 1).trimEnd() + "…" : txt;
};

function Crumbs({ title }) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-[#777] mb-4" data-testid="tournament-breadcrumbs">
      <ol className="flex items-center gap-1.5 flex-wrap">
        <li><Link to="/" className="hover:text-[#1E63C4] inline-flex items-center gap-1"><HomeIcon className="h-3.5 w-3.5" /> Home</Link></li>
        <li aria-hidden="true">/</li>
        <li><Link to="/tournaments" className="hover:text-[#1E63C4]">Tournaments</Link></li>
        <li aria-hidden="true">/</li>
        <li className="text-[#111] font-medium truncate max-w-[60vw]">{title}</li>
      </ol>
    </nav>
  );
}

function SummaryCard({ n, label }) {
  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-4 text-center">
      <div className="text-2xl font-extrabold text-[#0B0B0C]">{n}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#777] mt-1">{label}</div>
    </div>
  );
}

function LiveBlock({ live, ltid }) {
  const s = live.summary || {};
  return (
    <section className="space-y-5" data-testid="tournament-live-block">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard n={s.remaining ?? "—"} label="Players Remaining" />
        {live.is_double && <SummaryCard n={s.undefeated ?? "—"} label="Undefeated" />}
        {live.is_double && <SummaryCard n={s.one_loss ?? "—"} label="One Loss" />}
        <SummaryCard n={s.matches_complete ?? "—"} label="Matches Complete" />
      </div>
      {(live.up_next || []).length > 0 && (
        <div>
          <h3 className="font-heading font-bold text-lg mb-2">Up Next</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {live.up_next.slice(0, 4).map((m, i) => (
              <div key={i} className="bg-white rounded-lg border border-[#E5E7EB] p-3 text-sm">
                <p className="font-semibold">{m.player1 || "TBD"} <span className="text-[#999]">vs.</span> {m.player2 || "TBD"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {(live.latest_results || []).length > 0 && (
        <div>
          <h3 className="font-heading font-bold text-lg mb-2">Latest Results</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {live.latest_results.slice(0, 4).map((m, i) => (
              <div key={i} className="bg-white rounded-lg border border-[#E5E7EB] p-3 text-sm">
                <span className={m.winner === m.player1 ? "font-bold" : ""}>{m.player1}</span> <span className="text-[#999]">vs.</span> <span className={m.winner === m.player2 ? "font-bold" : ""}>{m.player2}</span>
                {m.scores && <span className="text-[#777]"> · {m.scores}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <Link
        to={`/tournaments/${ltid}/live`}
        onClick={() => track("follow_live", { ltid })}
        data-testid="tournament-open-live"
        className="inline-flex items-center gap-2 bg-[#E11D2A] hover:bg-[#c41320] text-white rounded-md px-5 py-2.5 font-bold text-sm transition-colors"
      >
        <Radio className="h-4 w-4" /> Open full live view
      </Link>
    </section>
  );
}

function Standings({ live }) {
  if (!live || (live.standings || []).length === 0) return null;
  return (
    <section data-testid="tournament-final-standings">
      <h2 className="font-heading font-extrabold text-2xl mb-3">Final Standings</h2>
      <div className="grid gap-2 sm:grid-cols-2">
        {live.standings.slice(0, 8).map((p) => (
          <div key={p.rank} className="flex items-center gap-3 bg-white rounded-lg border border-[#E5E7EB] p-3">
            <span className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm ${p.rank === 1 ? "bg-[#F5A623] text-white" : p.rank <= 3 ? "bg-[#E5E7EB]" : "bg-[#F4F5F3] text-[#777]"}`}>{p.rank}</span>
            <span className="font-semibold">{p.name}</span>
            {p.rank === 1 && <Trophy className="h-4 w-4 text-[#F5A623] ml-auto" />}
          </div>
        ))}
      </div>
    </section>
  );
}

function StatusBadge({ status }) {
  if (status === "live")
    return <span data-testid="tournament-status-live" className="inline-flex items-center gap-2 bg-[#E11D2A] text-white rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide"><span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" /></span> Live</span>;
  if (status === "completed")
    return <span data-testid="tournament-status-completed" className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide"><Trophy className="h-3.5 w-3.5 text-[#F5A623]" /> Completed</span>;
  if (status === "cancelled")
    return <span className="inline-flex items-center gap-2 bg-[#777] text-white rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide">Cancelled</span>;
  if (status === "postponed")
    return <span className="inline-flex items-center gap-2 bg-amber-500 text-white rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide">Postponed</span>;
  return <span data-testid="tournament-status-upcoming" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide"><CalendarDays className="h-3.5 w-3.5" /> Upcoming</span>;
}

export default function TournamentDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(undefined);
  const [signupOpen, setSignupOpen] = useState(false);
  const spotsLabel = useSpotsLabel(d?.tournament?.id, d?.status === "upcoming" && !!d?.tournament?.signups_enabled, signupOpen);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tournaments/detail/${slug}`);
      setD(data);
    } catch {
      setD(null);
    }
  }, [slug]);

  useEffect(() => {
    load();
    let timer;
    // Light polling only while live; stops once completed.
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get(`/tournaments/detail/${slug}`);
        setD(data);
        if (data.status !== "live") clearInterval(poll);
      } catch { /* ignore */ }
    }, 30000);
    timer = poll;
    return () => clearInterval(timer);
  }, [load, slug]);

  useEffect(() => {
    if (d && d.tournament) track("view_tournament", { slug, status: d.status });
  }, [d, slug]);

  if (d === undefined)
    return <Layout><div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1E63C4]" /></div></Layout>;

  if (d === null)
    return (
      <Layout>
        <Seo title="Tournament Not Found | Quinte BCA Pool League" noindex path={`/tournaments/${slug}`} />
        <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6" data-testid="tournament-404">
          <h1 className="font-heading font-extrabold text-3xl">Tournament not found</h1>
          <p className="text-[#777] mt-2 mb-6">We couldn't find that tournament. It may have been renamed or removed.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/tournaments" className="bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c]">All Tournaments</Link>
            <Link to="/divisions" className="border border-[#E5E7EB] rounded-md px-5 py-2.5 font-semibold hover:bg-[#F9FAF8]">Divisions</Link>
            <Link to="/" className="border border-[#E5E7EB] rounded-md px-5 py-2.5 font-semibold hover:bg-[#F9FAF8]">Home</Link>
          </div>
        </div>
      </Layout>
    );

  const t = d.tournament;
  const { status, live } = d;
  const path = `/tournaments/${t.slug || slug}`;
  const where = [t.venue, t.city || t.location, t.province].filter(Boolean).join(", ") || t.location;
  const flyer = t.image ? fileUrl(t.image) : undefined;

  const title = t.seo_title || (
    status === "live" ? `LIVE ${t.title} – Results & Bracket | Quinte BCA`
    : status === "completed" ? `${t.title} Results${t.date ? ` – ${t.date}` : ""} | Quinte BCA`
    : `${t.title}${t.city ? ` – ${t.city}, Ontario` : ""} | Quinte BCA`
  );
  const description = t.seo_description || (
    status === "live" ? `Follow the Quinte BCA ${t.title} live with current players, upcoming matches, latest results, eliminations and bracket updates.`
    : status === "completed" ? `View final results, participants and details from the Quinte BCA ${t.title}${t.date ? `, held ${t.date}` : ""}${where ? ` in ${t.city || t.location}` : ""}.`
    : `Register for the Quinte BCA ${t.title}${where ? ` at ${where}` : ""}. ${clip(t.description, 90) || "View format, Fargo limits, entry details and player list."}`
  ).slice(0, 300);

  const eventStatusMap = { cancelled: "https://schema.org/EventCancelled", postponed: "https://schema.org/EventPostponed" };
  const eventLd = {
    "@context": "https://schema.org", "@type": "Event",
    name: t.title,
    description: clip(t.description, 300) || description,
    eventStatus: eventStatusMap[status] || "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    url: SITE_URL + path,
    organizer: { "@type": "Organization", name: "Quinte BCA Pool League", url: SITE_URL },
    ...(flyer ? { image: flyer } : {}),
    ...(t.start_datetime ? { startDate: t.start_datetime } : {}),
    ...(t.end_datetime ? { endDate: t.end_datetime } : {}),
    ...(where ? { location: { "@type": "Place", name: t.venue || t.location, address: [t.city || t.location, t.province].filter(Boolean).join(", ") } } : {}),
  };
  const crumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Tournaments", item: `${SITE_URL}/tournaments` },
      { "@type": "ListItem", position: 3, name: t.title, item: SITE_URL + path },
    ],
  };

  return (
    <Layout>
      <Seo title={title} description={description} path={path} image={flyer} type="article" jsonLd={[eventLd, crumbLd]} />

      <section className="bg-[#0B0B0C] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <StatusBadge status={status} />
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-3" data-testid="tournament-title">{t.title}</h1>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-gray-300 text-sm mt-3">
            {t.date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {t.date}</span>}
            {where && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {where}</span>}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Crumbs title={t.title} />

        {/* LIVE first on mobile */}
        {status === "live" && live && <div className="mb-8"><LiveBlock live={live} ltid={t.live_ltid} /></div>}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {flyer && (
              <img src={flyer} alt={`${t.title} tournament flyer`} loading="lazy" className="w-full rounded-xl border border-[#E5E7EB]" />
            )}
            {(status === "completed") && (t.champion || t.result) && (
              <section className="bg-white rounded-xl border border-[#E5E7EB] p-5" data-testid="tournament-results">
                <h2 className="font-heading font-extrabold text-xl mb-2 flex items-center gap-2"><Trophy className="h-5 w-5 text-[#F5A623]" /> Results</h2>
                {t.champion && <p className="text-sm"><span className="font-semibold">Champion:</span> {t.champion}</p>}
                {t.runner_up && <p className="text-sm"><span className="font-semibold">Runner-up:</span> {t.runner_up}</p>}
                {t.result && <RichText html={t.result} className="text-sm mt-2" testid="tournament-result-text" />}
              </section>
            )}
            {t.description && (
              <section>
                <h2 className="font-heading font-extrabold text-xl mb-2">Details</h2>
                <RichText html={t.description} className="text-sm" testid="tournament-description" />
              </section>
            )}
            {status === "completed" && <Standings live={live} />}
            {(d.players || []).length > 0 && (
              <section className="bg-white rounded-xl border border-[#E5E7EB] p-5" data-testid="tournament-registered-players">
                <TournamentPlayerList groups={d.players} testid="tournament-players" />
              </section>
            )}
            {(t.gallery || []).length > 0 && (
              <section>
                <h2 className="font-heading font-extrabold text-xl mb-3">Photos</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {t.gallery.map((g, i) => <img key={i} src={fileUrl(g)} alt={`${t.title} photo ${i + 1}`} loading="lazy" className="w-full h-auto rounded-lg border border-[#E5E7EB]" />)}
                </div>
              </section>
            )}
          </div>

          <aside className="space-y-5">
            {status === "upcoming" && t.signups_enabled && (
              <div data-testid="tournament-signup-wrap"><TournamentSignup eventId={t.id} title={t.title} open={signupOpen} onOpenChange={setSignupOpen} badge={spotsLabel} divisions={t.divisions || []} onRegistered={load} /></div>
            )}
            {status === "upcoming" && t.link && (
              <a href={t.link} target="_blank" rel="noopener noreferrer" onClick={() => track("tournament_register", { slug })} className="block text-center bg-[#1E63C4] text-white rounded-md px-5 py-3 font-semibold hover:bg-[#174e9c]" data-testid="tournament-register-link">Registration / Info <ExternalLink className="inline h-4 w-4 ml-1" /></a>
            )}
            {t.replay_url && (
              <a href={t.replay_url} target="_blank" rel="noopener noreferrer" onClick={() => track("watch_tournament_video", { slug })} className="flex items-center justify-center gap-2 border border-[#E5E7EB] rounded-md px-5 py-3 font-semibold hover:bg-[#F9FAF8]"><Play className="h-4 w-4" /> Watch replay</a>
            )}
            {t.recap_url && (
              <a href={t.recap_url} className="flex items-center justify-center gap-2 border border-[#E5E7EB] rounded-md px-5 py-3 font-semibold hover:bg-[#F9FAF8]">Read recap</a>
            )}
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 text-sm space-y-1.5">
              <p className="font-heading font-bold mb-1 flex items-center gap-1.5"><Users className="h-4 w-4 text-[#1E63C4]" /> More Quinte BCA</p>
              <Link to="/tournaments" className="block text-[#1E63C4] hover:underline">All tournaments</Link>
              <Link to="/divisions" className="block text-[#1E63C4] hover:underline">League divisions & stats</Link>
              <Link to="/videos" className="block text-[#1E63C4] hover:underline">Match videos & replays</Link>
            </div>
          </aside>
        </div>

        {/* Prev / Next */}
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#E5E7EB]">
          {d.prev ? (
            <Link to={`/tournaments/${d.prev}`} className="inline-flex items-center gap-1.5 text-sm text-[#1E63C4] font-semibold hover:gap-2.5 transition-all" data-testid="tournament-prev"><ChevronLeft className="h-4 w-4" /> Previous tournament</Link>
          ) : <span />}
          {d.next ? (
            <Link to={`/tournaments/${d.next}`} className="inline-flex items-center gap-1.5 text-sm text-[#1E63C4] font-semibold hover:gap-2.5 transition-all" data-testid="tournament-next">Next tournament <ChevronRight className="h-4 w-4" /></Link>
          ) : <span />}
        </div>
      </div>
    </Layout>
  );
}
